import axios, { AxiosInstance, AxiosRequestConfig, AxiosPromise } from 'axios';
import { Observable } from 'rxjs';

export interface RxiosConfig extends AxiosRequestConfig {}

interface RequestArgs {
  method: HttpMethod;
  url: string;
  queryParams?: object;
  payload?: object;
  config?: AxiosRequestConfig;
}

enum HttpMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}

export class Rxios {
  private httpClient: AxiosInstance;

  constructor(options: RxiosConfig = {}) {
    this.httpClient = axios.create(options);
  }

  private doRequest<T>(args: RequestArgs) {
    const { method, url, queryParams, payload, config = {} } = args;
    let request: AxiosPromise<T>;
    switch (method) {
      case HttpMethod.GET:
        request = this.httpClient.get<T>(url, { params: queryParams, ...config });
        break;
      case HttpMethod.POST:
        request = this.httpClient.post<T>(url, payload, config);
        break;
      case HttpMethod.PUT:
        request = this.httpClient.put<T>(url, payload, config);
        break;
      case HttpMethod.PATCH:
        request = this.httpClient.patch<T>(url, payload, config);
        break;
      case HttpMethod.DELETE:
        request = this.httpClient.delete<T>(url, config);
        break;

      default:
        throw new Error('Method not supported');
    }
    return new Observable<T>((subscriber) => {
      request
        .then((response) => {
          subscriber.next(response.data);
        })
        .catch((err: Error) => {
          subscriber.error(err);
        })
        .finally(() => {
          subscriber.complete();
        });
    });
  }

  public get<T>(url: string, queryParams?: object, config?: AxiosRequestConfig) {
    return this.doRequest<T>({ method: HttpMethod.GET, url, queryParams, config });
  }

  public post<T>(url: string, payload: object, config?: AxiosRequestConfig) {
    return this.doRequest<T>({
      method: HttpMethod.POST,
      url,
      payload,
      config,
    });
  }

  public put<T>(url: string, payload: object, config?: AxiosRequestConfig) {
    return this.doRequest<T>({
      method: HttpMethod.PUT,
      url,
      payload,
      config,
    });
  }

  public patch<T>(url: string, payload: object, config?: AxiosRequestConfig) {
    return this.doRequest<T>({
      method: HttpMethod.PATCH,
      url,
      payload,
      config,
    });
  }

  public delete<T>(url: string, config?: AxiosRequestConfig) {
    return this.doRequest<T>({
      method: HttpMethod.DELETE,
      url,
      config,
    });
  }
}

export default Rxios;
